import { type InvoiceFormOptions } from '@utazzo/shared';
import {
  UzCard,
  UzCheckbox,
  UzCombobox,
  UzDatePicker,
  UzInput,
  UzNumberInput,
  UzSelect,
  type UzSelectOption,
} from '@utazzo/ui';
import React, { useMemo } from 'react';

import { type FareType } from './invoice-form.helpers';
import { InvoiceCityPicker } from './InvoiceCityPicker';

import {
  useAgentOptionsForLeads,
  useBranchOptions,
  useDestinationOptionsForLeads,
  useQuerySourceOptions,
} from '@/features/leads/use-lead-options';

/**
 * The destination master's option label is `"{name} - {city, state, country}"`.
 * The invoice persists the destination as a plain string (there is no FK), so
 * we store only the leading destination name - matching what the legacy city
 * picker saved and keeping printed invoices clean.
 */
const destinationNameFromLabel = (label: string): string => label.split(' - ')[0]!.trim();

/** Placeholder id for a value loaded from a saved invoice: the invoice stores
 *  only the city / destination NAME, so there is no master id to select. The
 *  label is what gets saved, so this id never reaches the API. */
const SAVED_VALUE_ID = '__saved__';

interface BookingInfoSectionProps {
  options: InvoiceFormOptions | undefined;
  fareType: FareType | '';
  onFareType: (v: FareType) => void;
  isMultiCity: boolean;
  departureCityId: string | null;
  departureCityLabel: string | null;
  onDepartureCity: (next: { id: string | null; label: string | null }) => void;
  destinationCityId: string | null;
  destinationCityLabel: string | null;
  onDestinationCity: (next: { id: string | null; label: string | null }) => void;
  departureDate: Date | null;
  onDepartureDate: (v: Date | null) => void;
  returnDate: Date | null;
  onReturnDate: (v: Date | null) => void;
  noOfPax: number | '';
  onNoOfPax: (v: number | '') => void;
  // Miscellaneous
  leadGenerationDate: Date | null;
  onLeadGenerationDate: (v: Date | null) => void;
  assignedAgentId: string;
  onAssignedAgentId: (v: string) => void;
  branchId: string;
  onBranchId: (v: string) => void;
  querySource: string;
  onQuerySource: (v: string) => void;
  isTravelAgent: boolean;
  onIsTravelAgent: (v: boolean) => void;
  travelAgentName: string;
  onTravelAgentName: (v: string) => void;
}

/**
 * Sections 3 + 3b - Booking info and Miscellaneous. Return date is enabled
 * only for Return fares; MultiCity hides the single departure/destination/date
 * (those are captured in the multicity grid).
 */
export const BookingInfoSection = ({
  options,
  fareType,
  onFareType,
  isMultiCity,
  departureCityId,
  departureCityLabel,
  onDepartureCity,
  destinationCityId,
  destinationCityLabel,
  onDestinationCity,
  departureDate,
  onDepartureDate,
  returnDate,
  onReturnDate,
  noOfPax,
  onNoOfPax,
  leadGenerationDate,
  onLeadGenerationDate,
  assignedAgentId,
  onAssignedAgentId,
  branchId,
  onBranchId,
  querySource,
  onQuerySource,
  isTravelAgent,
  onIsTravelAgent,
  travelAgentName,
  onTravelAgentName,
}: BookingInfoSectionProps): React.JSX.Element => {
  const isReturn = fareType === 2;

  const fareOptions = useMemo<ReadonlyArray<UzSelectOption<string>>>(
    () => (options?.fareTypes ?? []).map((f) => ({ value: String(f.code), label: f.name })),
    [options?.fareTypes],
  );

  const { options: agentOptions, isLoading: agentsLoading } = useAgentOptionsForLeads();
  const { options: branchOptions, isLoading: branchesLoading } = useBranchOptions();
  const { options: rawSourceOptions, isLoading: sourcesLoading } = useQuerySourceOptions();
  // Destination now reads from the Destinations master (Masters > Destinations),
  // exactly like the lead-creation form - not the cities master. The lead form
  // switched on 2026-06-24, so a destination like Vietnam shows here even when
  // there is no matching active city row.
  const { options: destinationOptions, isLoading: destinationsLoading } =
    useDestinationOptionsForLeads();
  // The invoice stores the destination as a free-text string. On edit we only
  // have that string (no id), so recover the selected option by matching the
  // stored name against each option's leading name - falling back to the full
  // label for any legacy value saved in the composite form.
  const destinationValue = useMemo<string | undefined>(() => {
    if (destinationCityId) return destinationCityId;
    const stored = destinationCityLabel?.trim();
    if (!stored) return undefined;
    const match = destinationOptions.find(
      (o) => destinationNameFromLabel(o.label) === stored || o.label === stored,
    );
    // No master match (renamed / legacy name): still show what was saved.
    return match?.value ?? SAVED_VALUE_ID;
  }, [destinationCityId, destinationCityLabel, destinationOptions]);
  const destinationComboOptions = useMemo(() => {
    const opts = destinationOptions.map((o) => ({ value: o.value, label: o.label }));
    const stored = destinationCityLabel?.trim();
    return destinationValue === SAVED_VALUE_ID && stored
      ? [{ value: SAVED_VALUE_ID, label: stored }, ...opts]
      : opts;
  }, [destinationOptions, destinationValue, destinationCityLabel]);
  // The invoice stores `querySource` as a plain name string, so key the select by
  // the source name (master names are unique) - no id round-trip needed.
  const sourceOptions = useMemo<ReadonlyArray<UzSelectOption<string>>>(
    () => rawSourceOptions.map((o) => ({ value: o.label, label: o.label })),
    [rawSourceOptions],
  );

  return (
    <UzCard title="Booking info" description="Trip details and how the query came in.">
      <div className="space-y-5">
        <div className="grid gap-4 md:grid-cols-3">
          <UzSelect
            label="Fare type"
            placeholder="Select fare type"
            options={fareOptions}
            value={fareType === '' ? undefined : String(fareType)}
            onValueChange={(v) => onFareType(Number(v) as FareType)}
          />
          {!isMultiCity && (
            <>
              <InvoiceCityPicker
                label="Departure city"
                scopeToHome
                // Edit loads only the saved name (no city id): still show it.
                value={departureCityId ?? (departureCityLabel ? SAVED_VALUE_ID : null)}
                valueLabel={departureCityLabel}
                onChange={onDepartureCity}
              />
              <div className="min-w-0">
                <UzCombobox
                  label="Destination city"
                  placeholder={
                    destinationsLoading
                      ? 'Loading...'
                      : destinationOptions.length === 0
                        ? 'No destinations available'
                        : 'Pick a destination'
                  }
                  searchPlaceholder="Search destinations..."
                  options={destinationComboOptions}
                  value={destinationValue}
                  onValueChange={(v) => {
                    if (v === SAVED_VALUE_ID) return;
                    const opt = destinationOptions.find((o) => o.value === v);
                    onDestinationCity({
                      id: v || null,
                      label: opt ? destinationNameFromLabel(opt.label) : null,
                    });
                  }}
                  disabled={destinationsLoading || destinationOptions.length === 0}
                />
              </div>
              <UzDatePicker
                label="Departure / check-in date"
                value={departureDate}
                onValueChange={onDepartureDate}
              />
              <UzDatePicker
                label="Return / check-out date"
                value={returnDate}
                onValueChange={onReturnDate}
                disabled={!isReturn}
                minDate={departureDate ?? undefined}
                hint={isReturn ? undefined : 'Return fares only'}
              />
            </>
          )}
          <UzNumberInput
            label="No. of pax"
            precision={0}
            min={0}
            value={noOfPax}
            onValueChange={(v) => onNoOfPax(v ?? '')}
          />
        </div>

        <div className="border-t border-zinc-100 pt-4">
          <p className="mb-3 text-xs font-medium tracking-wide text-zinc-500 uppercase">
            Miscellaneous
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <UzDatePicker
              label="Lead generation date"
              value={leadGenerationDate}
              onValueChange={onLeadGenerationDate}
            />
            <UzSelect
              label="Agent"
              placeholder={agentsLoading ? 'Loading...' : 'Select agent'}
              options={agentOptions}
              value={assignedAgentId || undefined}
              onValueChange={onAssignedAgentId}
              disabled={agentsLoading}
            />
            <UzSelect
              label="Branch"
              placeholder={branchesLoading ? 'Loading...' : 'Select branch'}
              options={branchOptions}
              value={branchId || undefined}
              onValueChange={onBranchId}
              disabled={branchesLoading}
            />
            <UzSelect
              label="Source"
              placeholder={sourcesLoading ? 'Loading...' : 'Select source'}
              options={sourceOptions}
              value={querySource || undefined}
              onValueChange={onQuerySource}
              disabled={sourcesLoading}
            />
          </div>
          <div className="mt-3 space-y-2">
            <UzCheckbox
              label="Booked via travel agent"
              checked={isTravelAgent}
              onCheckedChange={(c) => onIsTravelAgent(Boolean(c))}
            />
            {isTravelAgent && (
              <UzInput
                label="Travel agent name"
                value={travelAgentName}
                onChange={(e) => onTravelAgentName(e.target.value)}
              />
            )}
          </div>
        </div>
      </div>
    </UzCard>
  );
};
